import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { Normative } from "../../../../../http/border/types/normatives";
import {
  AdminBorderSystemNormative,
  UpsertBorderSystemNormativeBody,
} from "../../../../../http/border/types/systemNormatives";
import { useNormativesApi } from "../../../../../http/border/useNormatives";
import { useSystemNormativesApi } from "../../../../../http/border/useSystemNormatives";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import { CustomAsyncSelect } from "../../../../ui/select/CustomSelect";
import Switch from "../../../../ui/Switch";

interface SystemNormativeFormBody {
  normative: Normative | null;
  isHighWindVisible: boolean;
}

interface SystemNormativeFormProps {
  systemNormative?: AdminBorderSystemNormative;
  onClose: () => void;
  isDisabled?: boolean;
}

const SystemNormativeForm: FC<SystemNormativeFormProps> = ({
  systemNormative,
  onClose,
  isDisabled,
}: SystemNormativeFormProps) => {
  const i18n = useI18n();
  const { borderSystemId } = useParams<{ borderSystemId: string }>();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [defaultOptions, setDefaultOptions] = useState<Normative[]>([]);

  const normativesApi = useNormativesApi();
  const systemNormativesApi = useSystemNormativesApi();

  const defaultValues: SystemNormativeFormBody = {
    normative: systemNormative?.normative ?? null,
    isHighWindVisible: systemNormative?.isHighWindVisible ?? false,
  };

  const onSubmit = () => async (formData: SystemNormativeFormBody) => {
    setIsSubmitting(true);

    const body: UpsertBorderSystemNormativeBody = {
      systemId: borderSystemId!,
      normativeId: formData.normative?.id!,
      isHighWindVisible: formData.isHighWindVisible,
    };

    if (systemNormative?.id) {
      try {
        await systemNormativesApi.updateAdminSystemNormative(systemNormative.id, body);
        onClose();
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      try {
        await systemNormativesApi.insertAdminSystemNormative(body);
        onClose();
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Form<SystemNormativeFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isDisabled || isSubmitting}
      secondaryButton={
        <Button buttonProps={{ onClick: () => onClose() }} type="secondary">
          {i18n.translation.common.cancel}
        </Button>
      }
      onSubmit={onSubmit}
    >
      {({ control, setValue, watch, reset }) => {
        const isHighWindVisible = !!watch("isHighWindVisible");

        useEffect(() => {
          reset(defaultValues);
        }, [systemNormative]);

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="normative"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.normatives.singular} error={fieldState.error}>
                  {({ labelId }) => {
                    const searchTerm = useRef("");
                    const aborter = useRef(new AbortController());

                    const loadOptions = createLoadOptions(aborter, searchTerm, normativesApi.getAdminNormatives);

                    useEffect(() => {
                      loadOptions(searchTerm.current).then(setDefaultOptions);
                    }, []);

                    return (
                      <CustomAsyncSelect
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultOptions}
                        isDisabled={isDisabled || isSubmitting || !!systemNormative}
                        loadOptions={loadOptions}
                        getOptionLabel={(x) => x.name}
                        getOptionValue={(x) => x.id}
                        isInvalid={fieldState.invalid}
                        onChange={(value) => setValue("normative", value)}
                      />
                    );
                  }}
                </FormField>
              )}
            />

            <FormField label={i18n.translation.common.isHighWindVisible} isOptional>
              {({ labelId }) => (
                <Switch
                  disabled={isDisabled || isSubmitting}
                  id={labelId}
                  checked={isHighWindVisible}
                  onChange={(checked) => setValue("isHighWindVisible", checked)}
                />
              )}
            </FormField>
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default SystemNormativeForm;
